"use client";

import React from "react";
import { MapPin, Phone, Mail, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
const contactDetails = {
  address: "123 Expedition Way, Tropical Port, TP 54321",
  phone: "+1 (555) 987-6543",
  email: "explore@wanderlustexpeditions.com"
};
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-accent p-8 border border-border rounded-md">
        <h3 className="font-default text-2xl font-bold text-foreground mb-4">
          Inquiry Received!
        </h3>
        <p className="font-default text-muted-foreground mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md font-default uppercase px-8 py-4 h-auto border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground uppercase tracking-wider text-xs font-semibold">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Enter your explorer name" className="rounded-md border-0 border-b-2 border-muted focus-visible:ring-0 focus-visible:border-primary bg-background transition-colors" {...field} />
            </FormControl>
            <FormDescription className="font-default text-xs opacity-70">
              How should we address you on this journey?
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground uppercase tracking-wider text-xs font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="explorer@world.com" className="rounded-md border-0 border-b-2 border-muted focus-visible:ring-0 focus-visible:border-primary bg-background transition-colors" {...field} />
              </FormControl>
              <FormDescription className="font-default text-xs opacity-70">
                Where should we send your itinerary?
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-foreground uppercase tracking-wider text-xs font-semibold">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (000) 000-0000" className="rounded-md border-0 border-b-2 border-muted focus-visible:ring-0 focus-visible:border-primary bg-background transition-colors" {...field} />
              </FormControl>
              <FormDescription className="font-default text-xs opacity-70">
                Optional – for urgent expedition updates.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground uppercase tracking-wider text-xs font-semibold">Desired Climate</FormLabel>
            <FormControl>
              <Input placeholder="Ex.: Tropical, Arctic, Desert" className="rounded-md border-0 border-b-2 border-muted focus-visible:ring-0 focus-visible:border-primary bg-background transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide more details (min 10 chars)"
      },
      maxLength: {
        value: 1000,
        message: "Message is too long (max 1000 chars)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-foreground uppercase tracking-wider text-xs font-semibold">Expedition Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your dream vacation..." className="rounded-md border-0 border-b-2 border-muted focus-visible:ring-0 focus-visible:border-primary bg-background min-h-[150px] resize-y transition-colors" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-default">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-md font-default font-bold uppercase px-10 py-6 h-auto text-base tracking-widest shadow-lg transition-all hover:scale-105">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Preparing...
          </> : "Submit Inquiry"}
      </Button>
    </Form>;
}
export default function StartYourJourney() {
  return <section data-section-id="224961" id="start-your-journey" className="bg-background text-foreground py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {}
          <div className="flex flex-col justify-center">
            <h2 className="font-default text-5xl md:text-7xl font-bold text-foreground mb-6 uppercase tracking-tighter">
              Start Your <span className="text-primary">Journey</span>
            </h2>
            <p className="font-default text-xl text-muted-foreground mb-12 max-w-md leading-relaxed">
              Connect with our expedition specialists to craft your bespoke adventure or inquire about our upcoming group departures.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-md bg-accent flex items-center justify-center shrink-0 border border-primary/20 shadow-md">
                  <MapPin className="size-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-bold text-foreground mb-1 uppercase tracking-wide">
                    Base Camp
                  </h3>
                  <p className="font-default text-muted-foreground leading-relaxed">
                    {contactDetails.address}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-md bg-accent flex items-center justify-center shrink-0 border border-primary/20 shadow-md">
                  <Phone className="size-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-bold text-foreground mb-1 uppercase tracking-wide">
                    Satellite Line
                  </h3>
                  <Link to={`tel:${contactDetails.phone.replace(/\D/g, "")}`} className="font-mono text-muted-foreground hover:text-primary transition-colors text-lg">
                    {contactDetails.phone}
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-md bg-accent flex items-center justify-center shrink-0 border border-primary/20 shadow-md">
                  <Mail className="size-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-default text-lg font-bold text-foreground mb-1 uppercase tracking-wide">
                    Dispatch
                  </h3>
                  <Link to={`mailto:${contactDetails.email}`} className="font-mono text-muted-foreground hover:text-primary transition-colors text-lg">
                    {contactDetails.email}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="bg-card p-8 md:p-12 shadow-2xl border border-border rounded-md backdrop-blur-md">
            <h3 className="font-default text-3xl font-bold text-foreground mb-8 uppercase tracking-tight">
              Expedition Inquiry
            </h3>
            <FormProvider formKey="start_journey_form_contact" formId="start_journey_form_contact" sectionName="start-your-journey" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 1000
            }
          }} submitText="Submit Inquiry" successMessage="Your signal has been received! An expedition leader will contact you within 24 hours.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}